<template>
  <v-main
    v-if="!familyLoader"
    class="d-print-block"
  >
    <div class="title-background hidden-print-only">
      <TitleHeader
        :title="$t('family.subtitle')"
        :subtitle="$t('family.title')"
      />
    </div>
    <v-container
      v-if="family != null"
      id="family-page"
      class="pb-0 mb-12 d-print-block"
    >
      <v-row
        no-gutters
        class="mt-3 d-print-block"
      >
        <v-col
          cols="12"
          class="mb-3 hidden-print-only"
        >
          <ReturnLink
            :label="returnLink.label"
            :to-name="returnLink.toName"
            :params="returnLink.params"
          />
        </v-col>
        <v-col
          v-if="isChosen"
          cols="12"
          class="mb-10 mb-md-12 hidden-print-only"
          style="min-height: 74px;"
        >
          <div ref="chosenAlertBox">
            <InfoBox
              :text="$t('family.hasDonor')"
            />
          </div>
        </v-col>
        <v-col
          cols="12"
          class="print-break-avoid hidden-screen-only d-print-block mb-3"
        >
          <p class="font-weight-medium f-20 sz-primary-grey--text mb-1">
            {{ $t('family.printInfo.important') }}
          </p>
          <ul class="ml-5">
            <li
              v-for="(item, index) in $tm('family.printInfo.1')"
              :key="index"
              class="f-18 sz-primary-grey--text mb-0"
              v-html="item"
            />
          </ul>
        </v-col>
        <v-col
          cols="12"
          class="sz-primary-background mb-10 d-print-block print-break-avoid"
        >
          <v-row
            no-gutters
            class="pa-3"
          >
            <v-col
              cols="12"
              sm="8"
              md="8"
            >
              <TagCustom
                v-for="(item, index) in criteriaList"
                :key="index"
                :text="item"
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
              md="4"
              class="text-sm-right mt-3 mt-sm-1 hidden-print-only"
            >
              <p class="f-24 sz-primary-grey--text font-weight-medium mb-0">
                <span class="f-14 sz-secondary-grey--text font-weight-regular mr-1">
                  {{ $t('label.warehouse') }}:
                </span>
                {{ family.warehouse?.name }}
              </p>
            </v-col>
            <v-col
              cols="12"
              class="sz-primary-grey--text px-0 px-sm-3 mt-3"
            >
              <h1 class="text-h3 font-weight-medium">
                {{ familyTitle }}
              </h1>
              <p class="f-16 sz-primary-grey--text font-weight-medium mb-0 mt-1">
                <span class="f-14 sz-secondary-grey--text font-weight-regular mr-1">
                  {{ $t('label.familyCode') }}:
                </span>
                {{ family.familyEditionCode }}
              </p>
              <div class="border-bottom mt-5 mb-3" />
            </v-col>
            <v-col
              cols="12"
              class="px-0 px-sm-3"
            >
              <MembersComponent :list="family.familyMembers" />
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="12"
          class="mb-10 d-print-block"
        >
          <v-row
            no-gutters
            class="print-break-avoid d-print-block"
          >
            <v-col
              cols="12"
              md="6"
              class="sz-primary-grey--text"
            >
              <h2 class="f-24 font-weight-medium mb-1">
                {{ $t('family.history') }}
              </h2>
              <p class="f-16 font-weight-regular mb-0 pre-line">
                {{ family.sheet.description.familyHistory }}
              </p>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            class="mt-5 d-print-block print-break-avoid"
          >
            <v-col
              cols="12"
              md="6"
              class="sz-primary-grey--text family-beauty-border pa-4"
            >
              <h2 class="f-24 font-weight-medium mb-1">
                <v-icon
                  :size="25"
                  color="szRed"
                  class="mb-2 mr-1"
                >
                  $beauty
                </v-icon>
                {{ $t('family.familyEverydayLife') }}
              </h2>
              <p class="f-16 font-weight-regular mb-0 pre-line">
                {{
                  family?.sheet?.description?.familyEverydayLife }}
              </p>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            class="mt-5 d-print-block print-break-avoid"
          >
            <v-col
              cols="12"
              md="6"
              class="sz-primary-grey--text important-needs-border pa-4"
            >
              <h2 class="f-24 font-weight-medium mb-1 append-required">
                <v-icon
                  :size="25"
                  color="szRed"
                  class="mb-2 mr-1"
                >
                  $paczkopedia
                </v-icon>
                {{ $t('family.importantNeeds') }}
              </h2>
              <p
                class="d-block d-md-none mb-1 pt-2 pt-md-5 f-18 sz-secondary-grey--text prepend-required"
                v-html="$t('family.importantNeedsInfo')"
              />
              <div
                v-for="(item, index) in family.formattedNeeds.importantNeeds.list"
                :key="index"
                class="mb-3"
              >
                <p class="f-16 font-weight-regular mb-1">
                  <span class="d-inline">{{ index + 1 }}. </span>
                  <span
                    v-if="item?.need"
                    class="uppercase-first-letter d-inline"
                  >{{ item.need }}</span>
                </p>
                <p
                  v-if="item?.note"
                  class="f-14 font-weight-regular mb-0 pl-3 pre-line"
                >
                  {{ item.note }}
                </p>
              </div>
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="px-0 px-sm-5"
            >
              <p
                class="d-none d-md-block mb-1 pt-2 pt-md-5 f-18 sz-secondary-grey--text prepend-required"
                v-html="$t('family.importantNeedsInfo')"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="12"
          class="mb-10 d-print-block"
        >
          <OtherNeeds
            class="d-print-block"
            :formatted-needs="family.formattedNeeds"
          />
        </v-col>
        <v-col
          cols="12"
          class="sz-primary-background mb-10 d-print-block"
        >
          <WarehouseBox
            :warehouse="family?.warehouse"
            class="d-print-block print-break-avoid"
          />
        </v-col>
        <v-col
          cols="12"
          class="print-break-avoid hidden-screen-only d-print-block mb-3"
        >
          <p class="font-weight-medium f-20 sz-primary-grey--text mb-1">
            {{ $t('family.printInfo.remember') }}
          </p>
          <ul class="ml-5">
            <li
              v-for="(item, index) in $tm('family.printInfo.2')"
              :key="index"
              class="f-18 sz-primary-grey--text mb-0"
              v-html="item"
            />
          </ul>
        </v-col>
        <v-col
          cols="12"
          class="hidden-print-only"
        >
          <v-row
            no-gutters
            justify="space-between"
            align="center"
          >
            <v-col
              cols="12"
              sm="6"
              class="order-1 order-sm-0"
            >
              <ReturnLink
                :label="returnLink.label"
                :to-name="returnLink.toName"
                :params="returnLink.params"
                class="mr-0 my-3 mr-sm-10 d-block d-sm-inline-block text-sm-left text-center"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
              class="text-center text-sm-right order-0 order-sm-1"
            >
              <BaseButton
                v-if="!isChosen"
                :loading="donorLoader"
                :disabled="donorLoader"
                @click="chooseFamily"
              >
                {{ $t('family.chooseFamily') }}
              </BaseButton>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="py-0 hidden-print-only">
      <v-row no-gutters>
        <v-col
          cols="12"
          class="mb-12"
        >
          <v-divider />
        </v-col>
        <v-col cols="12">
          <FAQPanels :faq="FAMILIES_PAGE_FAQ" />
        </v-col>
      </v-row>
    </v-container>
    <FooterComponent class="mb" />
    <BottomSheetPersistent
      :visible="true"
      :is-chosen="isChosen"
      class="hidden-print-only"
      @submit="chooseFamily"
    />
    <ModalCustom v-model="isFamilyNotAvailable">
      <template #header>
        <p
          v-if="donorResponse.isFamilyChosen"
          class="f-24 mb-2 font-weight-medium"
        >
          {{ $t('family.chosenFamily') }}
        </p>
        <p
          v-else
          class="f-24 mb-2 font-weight-medium"
        >
          {{ $t('family.otherLocation') }}
        </p>
        <p
          v-if="donorResponse.isFamilyChosen"
          class="f-18 mb-0"
        >
          {{ $t('family.chooseOther') }}
        </p>
        <router-link
          :to="{ name: returnLink.toName, params: returnLink.params }"
          class="primary-button d-block d-md-inline-block pl-0 mt-5 mb-3 text-center"
        >
          {{ $t('label.findOtherFamily') }}
        </router-link>
      </template>
      <template #footer>
        <p class="mb-0">
          {{ $t('family.donateForSzp') }}
        </p>
        <a
          :id="paymentButtonId"
          :href="szpPaymentLink"
          target="_blank"
          class="secondary-button d-inline-block pl-0 mt-5 mb-3"
        >
          {{ $t('label.donateForSzp') }}
        </a>
      </template>
    </ModalCustom>
  </v-main>
  <LoaderComponent
    v-else
    page
  />
</template>

<script>
import MembersComponent from '@/components/FamiliesList/MembersComponent';
import BottomSheetPersistent from '@/components/Family/BottomSheetPersistent';
import OtherNeeds from '@/components/Family/OtherNeeds';
import WarehouseBox from '@/components/Family/WarehouseBox';
import BaseButton from '@/components/widgets/BaseButton.vue';
import FAQPanels from '@/components/widgets/FAQPanels';
import FooterComponent from '@/components/widgets/FooterComponent';
import InfoBox from '@/components/widgets/InfoBox.vue';
import LoaderComponent from '@/components/widgets/LoaderComponent';
import ModalCustom from '@/components/widgets/ModalCustom';
import ReturnLink from '@/components/widgets/ReturnLink';
import TagCustom from '@/components/widgets/TagCustom';
import TitleHeader from '@/components/widgets/TitleHeader';
import { LINKS } from '@/constants/externalLinks';
import { FAMILIES_PAGE_FAQ } from '@/constants/faq';
import { createCurrentFamilyEvent } from '@/plugins/gtm/events';
import storage from '@/services/storage';
import { useFamilyChoiceStore } from '@/stores/FamilyChoice';
import getLangsVariable from '@/utils/getLangsVariable';
import { useDisplay } from 'vuetify';
import { createChosenFamilyModalEvent, createUnavailableFamilyModalEvent } from '../plugins/gtm/events';
import ROUTE_NAMES from '@/constants/routeNames';

export default {
    name: 'FamilyPage',
    components: {
        ModalCustom,
        FAQPanels,
        FooterComponent,
        BottomSheetPersistent,
        WarehouseBox,
        LoaderComponent,
        InfoBox,
        OtherNeeds,
        ReturnLink,
        TitleHeader,
        TagCustom,
        MembersComponent,
        BaseButton
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.prevRoute = from && from.name;
            if (storage.get(storage.keys.bubble) != null) {
                vm.$store.commit('families/setBubbleId', storage.get(storage.keys.bubble));
            }
            if (vm.$route?.query?.context === 'family_choosen' || from.name === ROUTE_NAMES.THANKS) {
                vm.familyWasChosen = true;
            }
        });
    },
    beforeRouteLeave (to, from, next) {
        window.onscroll = null;
        window.onresize = null;
        this.$store.commit('family/setFamily', null);
        next();
    },
    beforeRouteUpdate (to, from, next) {
        this.fetchFamily(to.params);
        next();
    },
    props: {
        familyEditionId: {
            required: true
        },
        warehouseId: {
            required: true
        }
    },
    setup () {
        const familyChoiceStore = useFamilyChoiceStore();
        const display = useDisplay();

        const { smAndDown } = display;
        return {
            familyChoiceStore,
            smAndDown
        };
    },
    data () {
        return {
            FAMILIES_PAGE_FAQ,
            isFamilyNotAvailable: false,
            donorResponse: {
                isWarehouseValid: true,
                isFamilyChosen: false
            },
            familyWasChosen: false,
            prevRoute: null
        };
    },
    computed: {
        family () {
            return this.$store.getters['family/getFamily'];
        },
        bubbleId () {
            return this.$store.getters['families/getBubbleId'];
        },
        bubbleName () {
            return this.$store.getters['families/getBubbleName'];
        },
        provinceId () {
            return this.$store.getters['provinces/getCurrentProvinceId'];
        },
        provinceName () {
            return this.$store.getters['provinces/getCurrentProvinceName'];
        },
        filters () {
            return this.$store.getters['families/getFilter'];
        },
        familyLoader () {
            return this.$store.getters['family/getLoader'];
        },
        donorLoader () {
            return this.$store.getters['family/getDonorLoader'];
        },
        familyTitle () {
            const members = this.family.familyMembers;
            if (members.length > 0) {
                const firstMember = members[0].relationName + ' ' + members[0].newName;
                return members.length > 1 ? firstMember + ' ' + this.$t('familiesList.withFamily') : firstMember;
            }
            return '';
        },
        isChosen () {
            if (this.family) {
                return this.family.isChosen === true || this.familyWasChosen === true;
            }
            return false;
        },
        criteriaList () {
            const list = this.family.helpCriteria;
            if (this.family.hasGreaterNeeds === true) {
                list.push(this.$t('label.greaterNeeds'));
            }
            return list;
        },
        paymentBoxBottom () {
            return {
                title: getLangsVariable(['FAMILY', 'paymentBoxBottom', 'title']) || this.$t('family.paymentBoxBottom.title'),
                text: getLangsVariable(['FAMILY', 'paymentBoxBottom', 'text']) || this.$t('family.paymentBoxBottom.text'),
                imageUrl: getLangsVariable(['FAMILY', 'paymentBoxBottom', 'imageUrl']) || require('@/assets/images/reklama_wplata_SZP_2022_09_30.jpg'),
                link: {
                    href: getLangsVariable(['FAMILY', 'paymentBoxBottom', 'link', 'href']) || LINKS.szpPaymentForm,
                    text: getLangsVariable(['FAMILY', 'paymentBoxBottom', 'link', 'text']) || this.$t('label.donateForSzp'),
                    visible: getLangsVariable(['FAMILY', 'paymentBoxBottom', 'link', 'visible']) !== false
                },
                buttonId: 'swr-step2-payment-box-bottom'
            };
        },
        returnLink () {
            return {
                label: this.bubbleId ? this.$t('family.returnToFamiliesList') : this.$t('familiesList.returnToMainPage'),
                toName: this.bubbleId ? ROUTE_NAMES.FAMILIES_LIST : ROUTE_NAMES.HOME,
                params: this.bubbleId ? { bubbleId: this.bubbleId } : {}
            };
        },
        szpPaymentLink () {
            return this.donorResponse.isFamilyChosen ? LINKS.szpPaymentFormChosenFamily : LINKS.szpPaymentFormUnavailableFamily;
        },
        paymentButtonId () {
            return this.donorResponse.isFamilyChosen ? 'swr-step3-payment-chosen-family' : 'swr-step2-payment-no-family-in-warehouse';
        }
    },
    watch: {
        familyLoader (val) {
            if (val === false && this.isChosen === true) {
                this.$gtm.trackEvent(createCurrentFamilyEvent(this.isChosen));
            }
            if (val === false) {
                this.donorResponse.isWarehouseValid = this.family?.warehouse?.id === parseInt(this.warehouseId);
            }
        },
        donorResponse: {
            handler (val) {
                this.isFamilyNotAvailable = val.isWarehouseValid === false || val.isFamilyChosen === true;
                if (val.isWarehouseValid === false) {
                    this.$router.replace({
                        name: ROUTE_NAMES.FAMILY,
                        params: {
                            familyEditionId: this.family.familyEditionId,
                            warehouseId: this.family.warehouse.id
                        }
                    });
                }
            },
            deep: true
        },
        isFamilyNotAvailable (val) {
            if (val === false) {
                this.donorResponse.isWarehouseValid = true;
                this.donorResponse.isFamilyChosen = false;
            } else {
                const gtmEventValue = { familyEditionId: this.family.familyEditionId };
                const gtmEvent = this.donorResponse.isFamilyChosen
                    ? createChosenFamilyModalEvent(gtmEventValue)
                    : createUnavailableFamilyModalEvent(gtmEventValue);

                this.$gtm.trackEvent(gtmEvent);
            }
        },
        isChosen: {
            handler (val) {
                if (val === true) {
                    this.$nextTick(() => {
                        this.alertBoxOnScroll();
                        if (this.prevRoute === ROUTE_NAMES.FAMILIES_LIST) {
                            this.isFamilyNotAvailable = true;
                        }
                    });
                }
            },
            immediate: true
        }
    },
    created () {
        this.fetchFamily();
    },
    methods: {
        chooseFamily () {
            this.$store.dispatch('family/getDonor', {
                familyEditionId: this.familyEditionId,
                warehouseId: this.family.warehouse.id
            }).then((data) => {
                this.familyChoiceStore.setFamily(
                    {
                        familyEditionId: this.familyEditionId,
                        familyTitle: this.familyTitle,
                        familyWarehouse: this.family.warehouse,
                        warehouseId: this.family.warehouse.id,
                        captchaRequired: data.recaptcha_required
                    }
                );
                this.$router.push({
                    name: ROUTE_NAMES.FAMILY_CHOICE_FORM,
                    params: {
                        familyEditionId: this.familyEditionId,
                        warehouseId: this.family.warehouse.id
                    }
                });
            }).catch((error) => {
                if (error.response && error.response.status === 404) {
                    this.donorResponse.isWarehouseValid = error.response.data.data.isWarehouseValid;
                    this.donorResponse.isFamilyChosen = error.response.data.data.isFamilyChoosen;
                }
            });
        },
        fetchFamily (params = {}) {
            this.$store.dispatch('family/getFamily', {
                familyEditionId: params.familyEditionId || this.familyEditionId,
                warehouseId: params.warehouseId || this.warehouseId,
                bubbleId: storage.get(storage.keys.bubble) ? storage.get(storage.keys.bubble) : -1,
                filterWarehouseId: this.filters.warehouseId,
                helpCriteriaId: this.filters.categoryId,
                hasGreaterNeeds: this.filters.isDifficult
            });
        },
        alertBoxOnScroll () {
            const alertBox = this.$refs.chosenAlertBox;
            if (this.isChosen !== true || !alertBox || this.smAndDown) {
                return;
            }
            const offset = 30;
            const alertBoxTop = alertBox.offsetTop;
            const alertBoxParent = alertBox.parentElement;

            window.onresize = () => {
                alertBox.style.width = alertBoxParent.clientWidth + 'px';
            };
            window.onscroll = () => {
                if (window.scrollY + offset > alertBoxTop) {
                    alertBox.classList.add('fixed-element-scroll');
                    alertBox.style.top = offset + 'px';
                    alertBox.style.width = alertBoxParent.clientWidth + 'px';
                } else {
                    alertBox.classList.remove('fixed-element-scroll');
                    alertBox.style.top = '';
                }
            };
        }
    }
};
</script>

<style scoped></style>
